import { ActivityCountKey, ActivityMetrics } from '@/hooks/api/tenants/useGetTenantActivityMetrics';

export type ActivityFeature = keyof ActivityMetrics['featureUsage'];

const EVERY_USER = 'by any user or integration in this tenant';

const searchDescription = (kind: string) =>
  `One count per ${kind} search request against this tenant, ${EVERY_USER}. Fetching another page of results counts again.`;

export const FEATURE_DESCRIPTIONS: Record<ActivityFeature, string> = {
  search_text: searchDescription('text'),
  search_visual: searchDescription('visual (image)'),
  search_geometric: searchDescription('geometric (3D model)'),
  search_part: searchDescription('part-in-assembly'),
  search_composite: searchDescription('composite'),
  search_metadata: searchDescription('metadata'),
  compare: `One count each time a comparison is opened for a pair of models, ${EVERY_USER}, including when the result was already cached.`,
  download: `One count per asset file download, single file or dependency bundle, ${EVERY_USER}.`,
  folder_browse: `One count each time a folder's contents are listed, including the root folder, filters, sorts and result pages, ${EVERY_USER}.`,
  folder_manage: `One count per folder created, renamed, moved or deleted, ${EVERY_USER}.`,
  metadata_edit: `One count per metadata change on an asset, and per metadata field created, renamed or deleted, ${EVERY_USER}.`,
  viewer_open: `One count each time a model's viewer file is served, roughly once per model opened in the 3D viewer, ${EVERY_USER}.`,
  taxonomy_manage: `One count per taxonomy action: a taxonomy or category created, renamed, moved, imported or deleted, ${EVERY_USER}. One action counts once however many categories it touches, so a whole import counts once, as does deleting a category with everything under it.`,
  taxonomy_label: `One count per label a person applies to or removes from an asset, ${EVERY_USER}. Labels assigned by automatic classification are not counted.`,
  upload:
    'One count per asset created by a user, on the day it was created. Demo assets seeded by Physna are excluded.',
  report: 'One count per report created, on the day it was started.',
};

export const METRIC_DESCRIPTIONS: Record<ActivityCountKey, string> = {
  activeUsers:
    'Distinct users who made at least one authenticated request to this tenant on a day in the period. Counted by user id, never named. Physna staff are identified by their email domain.',
  searches: `All search types combined. One count per search request against this tenant, ${EVERY_USER}. Fetching another page of results counts again. See Features used for the split by type.`,
  compares: FEATURE_DESCRIPTIONS.compare,
  downloads: FEATURE_DESCRIPTIONS.download,
  uploads: FEATURE_DESCRIPTIONS.upload,
  reports: FEATURE_DESCRIPTIONS.report,
};

export const INTERNAL_USERS_NOTE =
  'Physna staff are counted here too. They appear when support or production operations work in your tenant, and they are counted the same way as your own users. Nobody is named either way.';

export const trendDescription = (days: number) =>
  `Change against the previous ${days} days. "New this period" means there was none of this in that earlier period, so there is no percentage to compare against.`;

export const newThisPeriodDescription = (days: number) => `Not used in the previous ${days} days`;

export const COUNTING_NOTE = 'Counts cover every user and integration in this tenant, UI and API alike, in UTC days.';
